import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

interface LayoutProps {
  children: React.ReactNode;
}

type NavLink = { label: string; path: string };
type NavParent = { label: string; path?: string; children: NavLink[]; header?: boolean };
type NavEntry = NavLink | NavParent;

const ADMIN_NAV: NavEntry[] = [
  { label: 'Overzicht', header: true, children: [
    { label: 'Dashboard', path: '/backoffice/dashboard' },
    { label: 'Berichten', path: '/backoffice/berichten' },
  ] },
  { label: 'Collectiviteiten & Leden', header: true, children: [
    { label: 'Collectiviteiten', path: '/backoffice/collectivities' },
    { label: 'Leden', path: '/backoffice/leden' },
    { label: 'Toegangsverzoeken', path: '/backoffice/toegangsverzoeken' },
  ] },
  { label: 'Diensten', header: true, children: [
    { label: 'Kredietopinies', path: '/backoffice/kredietopinies' },
    { label: 'Verzekeringen', path: '/backoffice/verzekeringen' },
    { label: 'Incasso', path: '/backoffice/incasso' },
    { label: 'Schade', path: '/backoffice/schade' },
  ] },
  { label: 'Financieel', header: true, children: [
    { label: 'Verrekeningen', path: '/backoffice/financieel' },
    { label: 'Boekstukken', path: '/backoffice/boekstukken' },
    { label: 'Instellingen', path: '/backoffice/financieel-instellingen' },
  ] },
  { label: 'Systeem', header: true, children: [
    { label: 'Instellingen', path: '/settlement-config' },
    { label: 'Templates', path: '/backoffice/templates' },
  ] },
];

const MEMBER_NAV: NavEntry[] = [
  { label: 'Dashboard', path: '/dashboard' },
  { label: 'Mijn debiteuren', path: '/debtors' },
  { label: 'Mijn facturen', path: '/invoices' },
  { label: 'Berichten', path: '/notifications' },
];

function flatLinks(entries: NavEntry[]): NavLink[] {
  return entries.flatMap((e) => {
    if ('children' in e) {
      const self: NavLink[] = e.path ? [{ label: e.label, path: e.path }] : [];
      return [...self, ...e.children];
    }
    return [e];
  });
}

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, accessToken, logoutUser } = useAuthStore();
  const isAdmin = user?.role === 'kva_admin';
  const navItems = isAdmin ? ADMIN_NAV : MEMBER_NAV;
  const homePath = isAdmin ? '/backoffice/dashboard' : '/dashboard';

  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (isAdmin || !accessToken) return;
    fetch(`${API_BASE_URL}/api/notifications/unread-count`, { headers: { Authorization: `Bearer ${accessToken}` } })
      .then((r) => (r.ok ? r.json() : { count: 0 }))
      .then((d) => setUnread(d.count || 0))
      .catch(() => setUnread(0));
  }, [isAdmin, accessToken, location.pathname]);

  const handleLogout = async () => {
    await logoutUser();
    navigate('/login');
  };

  const matchPath = (path: string) =>
    location.pathname === path || (path !== '/backoffice' && location.pathname.startsWith(path + '/'));

  const links = flatLinks(navItems);
  const currentTitle =
    links.find((n) => matchPath(n.path))?.label ??
    (isAdmin ? 'Backoffice' : 'MijnFactuurVerzekerd');

  useEffect(() => {
    document.title = `${currentTitle} · MijnFactuurVerzekerd`;
  }, [currentTitle]);

  const renderLink = (item: NavLink, indent = false) => {
    const active = matchPath(item.path);
    const badge = item.path === '/notifications' ? unread : 0;
    return (
      <button
        key={item.path}
        onClick={() => { navigate(item.path); setOpen(false); }}
        className={`flex w-full items-center gap-3 rounded-md py-2 text-sm text-left transition-colors ${indent ? 'pl-6 pr-3' : 'px-3'} ${
          active ? 'bg-white/15 text-white font-medium' : 'text-white/85 hover:bg-white/10 hover:text-white'
        }`}
      >
        <span className="flex-1">{item.label}</span>
        {badge > 0 && (
          <span className="rounded-full bg-mfb-orange text-white text-[10px] font-semibold px-1.5 py-0.5 min-w-[20px] text-center">
            {badge}
          </span>
        )}
      </button>
    );
  };

  const SideNav = (
    <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
      {navItems.map((entry) => {
        if ('children' in entry) {
          return (
            <div key={entry.label} className="pt-1">
              {entry.header ? (
                <div className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-widest text-white/40">{entry.label}</div>
              ) : (
                renderLink({ label: entry.label, path: entry.path! })
              )}
              {entry.children.map((it) => renderLink(it, true))}
            </div>
          );
        }
        return renderLink(entry);
      })}
    </nav>
  );

  const SideContent = (
    <>
      <div className="px-5 py-6 border-b border-white/10">
        <button onClick={() => navigate(homePath)} className="block w-full text-left">
          <img src="/logo-mijnfactuurverzekerd-wit.svg" alt="MijnFactuurVerzekerd" className="w-full max-w-[180px] h-auto" />
        </button>
        <div className="mt-4 text-[11px] uppercase tracking-widest text-white/60">
          {isAdmin ? 'Backoffice' : 'Leden-portal'}
        </div>
      </div>
      {SideNav}
      <div className="p-3 border-t border-white/10">
        {user && (
          <div className="px-3 py-2 text-xs text-white/70">
            <div className="font-medium text-white truncate">{user.full_name}</div>
            <div className="truncate">{user.company_name || user.email}</div>
          </div>
        )}
        <button
          onClick={handleLogout}
          className="mt-1 flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm text-white/85 hover:bg-white/10 hover:text-white"
        >
          Uitloggen
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-slate-50 font-sans">
      {/* Desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 w-60 bg-mfb-blue text-white hidden md:flex flex-col">
        {SideContent}
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-50 md:hidden" onClick={() => setOpen(false)}>
          <div className="absolute inset-0 bg-black/40" />
          <aside className="absolute inset-y-0 left-0 w-64 bg-mfb-blue text-white flex flex-col" onClick={(e) => e.stopPropagation()}>
            {SideContent}
          </aside>
        </div>
      )}

      <div className="md:pl-60">
        <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
          <div className="px-4 md:px-8 py-3 md:py-4 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <button className="md:hidden -ml-1 p-2 rounded-md hover:bg-slate-100" onClick={() => setOpen(true)} aria-label="Menu">
                <span className="text-xl leading-none">☰</span>
              </button>
              <div className="min-w-0">
                <div className="text-[11px] uppercase tracking-widest text-slate-400">
                  {isAdmin ? 'Backoffice' : 'Leden-portal'}
                </div>
                <h1 className="font-display text-xl md:text-2xl font-bold tracking-tight text-mfb-blue truncate">
                  {currentTitle}
                </h1>
              </div>
            </div>
            {!isAdmin && (
              <button onClick={() => navigate('/notifications')} className="relative p-2 rounded-md hover:bg-slate-100" title="Berichten">
                <span className="text-xl">🔔</span>
                {unread > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-mfb-orange text-white text-[10px] font-bold flex items-center justify-center">
                    {unread}
                  </span>
                )}
              </button>
            )}
          </div>
        </header>

        <main className="px-4 md:px-8 py-6 md:py-8 max-w-7xl mx-auto">{children}</main>
      </div>
    </div>
  );
};

export default Layout;
